import * as React from 'react';

import { cn } from '@rematter/ui-web';
import { cva } from 'class-variance-authority';
import startCase from 'lodash/startCase';
import { useDocumentTitle } from 'usehooks-ts';
import { Link, useLocation } from 'wouter';

import { useTranslations } from '../i18n/useTranslations';
import { NavItem } from '../routing/createModuleNav';
import { ModuleDefinition } from '../routing/types';

export type ModuleLayoutProps = {
  className?: string;
  /**
   * @deprecated Use `moduleNavigation` instead.
   */
  module?: ModuleDefinition;
  moduleNavigation?: NavItem;
  title?: string;
  standalone?: boolean;
  noContentPadding?: boolean;
  hideHeader?: boolean;
  children: React.ReactNode;
  showSecondLevelNav?: boolean;
};

export const ModuleLayout = (props: ModuleLayoutProps) => {
  const { module, children, noContentPadding } = props;

  const t = useTranslations();
  const [location] = useLocation();

  const moduleNavigation = props.moduleNavigation ?? module?.nav;

  const currentMenuItem = React.useMemo(
    () =>
      props.showSecondLevelNav
        ? moduleNavigation
        : moduleNavigation?.children?.find(({ route }) =>
            location.startsWith(route.path),
          ),
    [props.showSecondLevelNav, moduleNavigation, location],
  );

  const locationTitle = React.useMemo(
    () => location.split('/').filter(Boolean).map(startCase).join(' / '),
    [location],
  );

  useDocumentTitle(`Rematter Console – ${locationTitle}`);

  const currentSubMenu =
    currentMenuItem?.children.filter((it) => it.route.label) ?? [];

  return (
    <div className={cn('@ModuleLayout flex h-full flex-col', props.className)}>
      {/* Sub-navigation tabs */}
      {currentSubMenu.length > 0 && (
        <div className={navContainerVariants()}>
          <div className="px-4">
            <div className="flex space-x-3">
              {currentSubMenu.map(({ route }) => (
                <Link
                  key={route.path}
                  href={route.path}
                  className={linkVariants({
                    active:
                      location === route.path ||
                      location.startsWith(route.path + '/'),
                  })}
                >
                  {route.label?.(t)}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className={contentVariants({ noContentPadding })}>{children}</div>
    </div>
  );
};

const contentVariants = cva('flex flex-1 flex-col', {
  variants: {
    noContentPadding: {
      true: 'overflow-hidden',
      false: 'overflow-y-auto p-6',
    },
  },
  defaultVariants: {
    noContentPadding: false,
  },
});

// Define link styles using cva with dark mode
const linkVariants = cva(
  'group inline-flex items-center border-b-2 px-1 py-3 text-sm font-medium',
  {
    variants: {
      active: {
        true: 'border-primary/50 text-primary/60',
        false:
          'border-transparent text-neutral/50 hover:border-neutral/30 hover:text-neutral/70',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

// Define navigation container styles with dark mode
const navContainerVariants = cva('border-b border-neutral/10 bg-neutral/5');
